import { useCallback, useEffect, useRef, useState } from 'react';
import { autoplannerAPI } from '../services/api';

// Manage session overrides (get/save/clear), including optimistic local state
const useSessionOverrides = (taskId, options = {}) => {
  const { onSaved } = options;
  const [sessionOverrides, setSessionOverrides] = useState({}); // { sessionId: overrides }
  const [savingBySession, setSavingBySession] = useState({});
  const [optimisticSessions, setOptimisticSessions] = useState(new Set());
  // Dedup caches for prefetching
  const fetchedSessionIdsRef = useRef(new Set());
  const pendingPrefetchRef = useRef(new Map()); // sessionId -> Promise

  const getSessionOverride = useCallback(
    (sessionId) => {
      return sessionOverrides[sessionId] || sessionOverrides[String(sessionId)] || null;
    },
    [sessionOverrides]
  );

  const hasOverrides = useCallback(
    (sessionId) => {
      const entry = getSessionOverride(sessionId);
      if (!entry || typeof entry !== 'object') return optimisticSessions.has(sessionId);
      const mo = entry.member_overrides;
      const hasReal = mo && typeof mo === 'object' && Object.keys(mo).length > 0;
      return hasReal || optimisticSessions.has(sessionId);
    },
    [getSessionOverride, optimisticSessions]
  );

  const isSavingSession = useCallback(
    (sessionId) => Boolean(savingBySession[sessionId]),
    [savingBySession]
  );

  const saveSessionOverrides = useCallback(
    async (sessionId, payload) => {
      try {
        if (!taskId) return;
        setSavingBySession((s) => ({ ...s, [sessionId]: true }));
        const res = await autoplannerAPI.saveSessionOverrides(taskId, sessionId, payload);
        if (res?.success) {
          // Merge optimistically to local state
          setSessionOverrides((prev) => {
            const key = String(sessionId);
            const existing = prev[sessionId] || prev[key] || {};
            const existingMembers = existing.member_overrides || {};
            const incomingMembers = payload.member_overrides || {};
            const mergedMembers = { ...existingMembers, ...incomingMembers };
            const merged = { ...existing, ...payload, member_overrides: mergedMembers };
            return { ...prev, [sessionId]: merged, [key]: merged };
          });
          setOptimisticSessions((prev) => new Set([...prev, sessionId]));
          if (typeof onSaved === 'function') onSaved(sessionId);
        }
      } catch (e) {
        console.error('Failed saving session overrides', e);
      } finally {
        setSavingBySession((s) => ({ ...s, [sessionId]: false }));
      }
    },
    [taskId, onSaved]
  );

  const clearSessionOverride = useCallback(
    async (sessionId) => {
      try {
        if (!taskId) return;
        setSavingBySession((s) => ({ ...s, [sessionId]: true }));
        const res = await autoplannerAPI.clearSessionOverrides(taskId, sessionId);
        if (res?.success) {
          setSessionOverrides((prev) => {
            const next = { ...prev };
            delete next[sessionId];
            delete next[String(sessionId)];
            return next;
          });
          setOptimisticSessions((prev) => {
            const next = new Set(prev);
            next.delete(sessionId);
            return next;
          });
          if (typeof onSaved === 'function') onSaved(sessionId);
        }
      } catch (e) {
        console.error('Failed clearing session override', e);
      } finally {
        setSavingBySession((s) => ({ ...s, [sessionId]: false }));
      }
    },
    [taskId, onSaved]
  );

  const prefetchOverrides = useCallback(
    async (sessionIds = [], { force = false } = {}) => {
      try {
        if (!taskId) return;
        const incoming = Array.from(new Set((sessionIds || []).filter(Boolean)));
        if (incoming.length === 0) return;
        const idsToFetch = [];
        incoming.forEach((sid) => {
          const alreadyFetched = fetchedSessionIdsRef.current.has(sid);
          const inFlight = pendingPrefetchRef.current.has(sid);
          if (!force && (alreadyFetched || inFlight)) return;
          idsToFetch.push(sid);
        });
        if (idsToFetch.length === 0) return;

        const fetchOne = async (sid) => {
          try {
            const r = await autoplannerAPI.getSessionOverrides(taskId, sid);
            const overrides = r?.overrides || null;
            setSessionOverrides((prev) => {
              const next = { ...prev };
              if (
                overrides &&
                typeof overrides === 'object' &&
                Object.keys(overrides?.member_overrides || {}).length > 0
              ) {
                next[String(sid)] = overrides;
              } else {
                // Ensure key exists for empty overrides to prevent repeated fetches
                next[String(sid)] = overrides;
              }
              return next;
            });
            fetchedSessionIdsRef.current.add(sid);
          } catch (e) {
            // swallow; still mark as fetched to avoid hot loop; caller can force later
            fetchedSessionIdsRef.current.add(sid);
          } finally {
            pendingPrefetchRef.current.delete(sid);
          }
        };

        idsToFetch.forEach((sid) => {
          const p = fetchOne(sid);
          pendingPrefetchRef.current.set(sid, p);
        });
        await Promise.all(idsToFetch.map((sid) => pendingPrefetchRef.current.get(sid)));
      } catch (e) {
        console.error('Prefetch overrides failed', e);
      }
    },
    [taskId]
  );

  // Load all overrides for a task in one request by reading the task payload
  const loadAllOverrides = useCallback(
    async (taskIdArg) => {
      try {
        const id = taskIdArg || taskId;
        if (!id) return;
        const task = await autoplannerAPI.getAutoplannerTask(id);
        let parsed = {};
        try {
          if (task && task.session_overrides) {
            parsed = JSON.parse(task.session_overrides) || {};
          }
        } catch {
          parsed = {};
        }

        // Normalize into state: we store composite keys and also group by session id
        setSessionOverrides((prev) => {
          const next = { ...prev };
          const seenSessions = new Set();
          Object.entries(parsed).forEach(([compositeKey, override]) => {
            next[String(compositeKey)] = override;
            // Also mark session id as fetched to avoid redundant prefetches
            const [sid] = String(compositeKey).split('-');
            if (sid) {
              seenSessions.add(Number(sid));
              // Maintain a grouped entry by session id that nests composite keys
              const group = next[String(sid)] || {};
              group[String(compositeKey)] = override;
              next[String(sid)] = group;
            }
          });
          // Update dedup cache
          seenSessions.forEach((sid) => fetchedSessionIdsRef.current.add(sid));
          return next;
        });
      } catch (e) {
        console.error('Failed to load all session overrides', e);
      }
    },
    [taskId]
  );

  // Initial load when taskId available (optional: rely on callers to prefetch for specific sessions)
  useEffect(() => {
    // no-op: we keep lazy loading via prefetch
  }, [taskId]);

  return {
    sessionOverrides,
    hasOverrides,
    getSessionOverride,
    isSavingSession,
    optimisticSessions,
    saveSessionOverrides,
    clearSessionOverride,
    prefetchOverrides,
    loadAllOverrides,
  };
};

export default useSessionOverrides;
